import React from 'react';
import { useTranslation } from 'react-i18next';

import { DateStatusProperties } from '../../../../../../interface/timeline.interface';
import CustomTable from '../../../../../../shared-components/custom-table/CustomTable';

interface ConsumptionTableViewProperties {
  data: DateStatusProperties[];
  loading: boolean;
}

/**
 * Format decimal hours or string to formatted time string "X hrs Y mins"
 * @param {number | string | undefined} timeValue - Value to format
 * @returns {string} Formatted time string
 */
function formatHours(timeValue: number | string | undefined): string {
  if (timeValue === undefined || timeValue === null) {
    return '0 hrs 0 mins';
  }
  if (typeof timeValue === 'string') {
    if (timeValue.includes('hr') || timeValue.includes('min')) {
      return timeValue;
    }
    const parsed = parseFloat(timeValue);
    if (Number.isNaN(parsed)) {
      return '0 hrs 0 mins';
    }
    const hours = Math.floor(parsed);
    const minutes = Math.round((parsed - hours) * 60);
    return `${hours} hrs ${minutes} mins`;
  }
  const hours = Math.floor(timeValue);
  const minutes = Math.round((timeValue - hours) * 60);
  return `${hours} hrs ${minutes} mins`;
}

/**
 * Consumption Table View component displaying consumption statistics in table format
 * @param {ConsumptionTableViewProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered component
 */
function ConsumptionTableView(
  properties: ConsumptionTableViewProperties,
): React.ReactElement {
  const { data, loading } = properties;
  const { t } = useTranslation('devices');

  /**
   * Render formatted power consumed
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderPowerConsumed = (rowData: DateStatusProperties): React.ReactElement => {
    const power =
      typeof rowData?.powerConsumed === 'string'
        ? rowData.powerConsumed
        : `${Number(rowData?.energyDelivered ?? rowData?.powerConsumed ?? 0).toFixed(3)} kW`;
    return <p>{power}</p>;
  };

  /**
   * Render formatted active hours
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderActiveHours = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{formatHours(rowData?.onlineHours)}</p>
  );

  /**
   * Render formatted idle hours
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderIdleHours = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{formatHours(rowData?.offlineHours)}</p>
  );

  /**
   * Render formatted session count
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderSessionCount = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{rowData?.sessionCount ?? 0}</p>
  );

  /**
   * Render formatted revenue
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderRevenue = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{rowData?.totalRevenue !== undefined ? Number(rowData.totalRevenue).toFixed(2) : '0.00'}</p>
  );

  /**
   * Render formatted uptime
   * @param {DateStatusProperties} rowData - Current row data
   * @returns {React.ReactElement} Rendered element
   */
  const renderUptime = (rowData: DateStatusProperties): React.ReactElement => (
    <p>{rowData?.uptime || '0.00%'}</p>
  );

  const consumptionColumns = [
    {
      label: t('date', 'Date'),
      accessorKey: 'dateToShow' as keyof DateStatusProperties,
      columnWidth: 150,
    },
    {
      label: t('powerConsumed', 'Power Consumed'),
      accessorKey: 'powerConsumed' as keyof DateStatusProperties,
      columnWidth: 160,
      customData: renderPowerConsumed,
    },
    {
      label: t('activeHours', 'Active hours'),
      accessorKey: 'onlineHours' as keyof DateStatusProperties,
      columnWidth: 150,
      customData: renderActiveHours,
    },
    {
      label: t('idleHours', 'Idle hours'),
      accessorKey: 'offlineHours' as keyof DateStatusProperties,
      columnWidth: 150,
      customData: renderIdleHours,
    },
    {
      label: t('sessions', 'Sessions'),
      accessorKey: 'sessionCount' as keyof DateStatusProperties,
      columnWidth: 130,
      customData: renderSessionCount,
    },
    {
      label: t('revenue', 'Revenue'),
      accessorKey: 'totalRevenue' as keyof DateStatusProperties,
      columnWidth: 130,
      customData: renderRevenue,
    },
    {
      label: t('uptime', 'Uptime'),
      accessorKey: 'uptime' as keyof DateStatusProperties,
      columnWidth: 130,
      customData: renderUptime,
    },
  ];

  return (
    <div className="device-table-view-container">
      <CustomTable
        header={consumptionColumns}
        data={data}
        serialNo
        filter={{}}
        filterClearFunction={() => {}}
        filterFunction={() => {}}
        actionButton={false}
        limit={data.length || 31}
        page={1}
        loading={loading}
        totalPages={1}
        totalResults={data.length || 1}
        pageLimitChange={(value: any) => {
          console.log(value);
        }}
        sizeChangeButtonRequired
        disablePagination
        disableHeader
        height={window.innerHeight >= 700 ? 347 : 352}
      />
    </div>
  );
}

export default ConsumptionTableView;
